import React, { useState, useRef } from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { Panel } from 'primereact/panel';
import { Chip } from 'primereact/chip';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Fieldset } from 'primereact/fieldset';
import { TabView, TabPanel } from 'primereact/tabview';
import { Knob } from 'primereact/knob';
import { Message } from 'primereact/message';
import axiosInstance from '../../../config/axios.config';
import { scoreBand, formatIncome, SCORE_MAX } from './creditScore';

const ExperianOutputComponent = ({ reportData, onBack, onClose, serviceDetails, onDownloadPDF, pdfDownloadUrl: propPdfUrl, pdfPasswordHint: propPdfHint, hideCredits }) => {
        
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [paymentHistoryModal, setPaymentHistoryModal] = useState(false);
    const [selectedAccountHistory, setSelectedAccountHistory] = useState(null);
    const [selectedAccountNumber, setSelectedAccountNumber] = useState('');
    const [expandedRows, setExpandedRows] = useState(null);
    const toast = useRef(null);

    // Extract PDF URL and password hint - prioritize props, then check reportData
    // When coming from ReportHistory, props are passed directly
    const pdfDownloadUrl = propPdfUrl ||
                          reportData?.pdf_download_url ||
                          reportData?.pdf_url ||
                          reportData?.data?.pdf_download_url ||
                          reportData?.data?.pdf_url ||
                          reportData?.api_response?.pdf_download_url ||
                          reportData?.api_response?.pdf_url ||
                          reportData?.report_json?.pdf_download_url ||
                          reportData?.report_json?.pdf_url ||
                          reportData?.download_url ||
                          null;

    const pdfPasswordHint = propPdfHint ||
                           reportData?.pdf_password_hint ||
                           reportData?.data?.pdf_password_hint ||
                           reportData?.api_response?.pdf_password_hint ||
                           reportData?.report_json?.pdf_password_hint ||
                           null;

    // Debug logging to understand data structure
    console.log('Experian PDF Debug:', {
        'propPdfUrl': propPdfUrl,
        'propPdfHint': propPdfHint,
        'reportData.pdf_download_url': reportData?.pdf_download_url,
        'reportData.pdf_url': reportData?.pdf_url,
        'reportData.pdf_password_hint': reportData?.pdf_password_hint,
        'reportData.report_id': reportData?.report_id,
        'reportData.id': reportData?.id,
        'Final pdfDownloadUrl': pdfDownloadUrl,
        'Final pdfPasswordHint': pdfPasswordHint,
        'Full reportData': reportData
    });

    // Helper function to safely render any value
    const safeRender = (value) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        if (typeof value === 'object') {
            // Handle information objects with title and message
            if (value.title !== undefined || value.message !== undefined) {
                return String(value.message || value.title || '');
            }
            // For other objects, stringify them
            return JSON.stringify(value);
        }
        // For primitives, convert to string
        return String(value);
    };
    
    // Extract the actual data from the response structure
    // Handle multiple possible data structures from API and database
    let mainData = reportData;
        // First check for fresh API response: data.data[0].creditReport
    if (reportData?.data?.data?.[0]?.creditReport) {
        console.log('Experian: Found data in data.data[0].creditReport (fresh API response)');
        mainData = reportData.data.data[0].creditReport;
    }
    // Check for direct array structure: data[0].creditReport  
    else if (reportData?.data?.[0]?.creditReport) {
        console.log('Experian: Found data in data[0].creditReport (direct array)');
        mainData = reportData.data[0].creditReport;
    }
    // Backup check for api_response path
    else if (reportData?.api_response?.data?.data?.[0]?.creditReport) {
        console.log('Experian: Found data in api_response.data.data[0].creditReport');
        mainData = reportData.api_response.data.data[0].creditReport;
    }
    // Check if reportData itself contains the actual report structure
    else if (reportData?.personalInformation || reportData?.score || reportData?.reportSummary) {
        // Data is directly in reportData
        mainData = reportData;
    } 
    // IMPORTANT: Check for Report History structure - report_json.data.raw_data.data
    else if (reportData?.report_json?.data?.raw_data?.data) {
        console.log('Experian: Found data in report_json.data.raw_data.data (Report History structure)');
        mainData = reportData.report_json.data.raw_data.data;
    }
    // Check for the structure when coming from database: data.raw_data.data
    else if (reportData?.data?.raw_data?.data) {
        console.log('Experian: Found data in data.raw_data.data');
        mainData = reportData.data.raw_data.data;
    }
    // Check various possible nested data locations
    else if (reportData?.data) {
        mainData = reportData.data;
    } else if (reportData?.raw_data?.data) {
        mainData = reportData.raw_data.data;
    } else if (reportData?.report_json?.data) {
        mainData = reportData.report_json.data;
    } else if (reportData?.api_response?.data) {
        mainData = reportData.api_response.data;
    }
    // Additional check for deeply nested structures
    else if (reportData?.data?.data) {
        mainData = reportData.data.data;
    } else if (reportData?.response?.data) {
        mainData = reportData.response.data;
    }
        // Extract all sections from the main data
    const personalInfo = mainData?.personalInformation || {};
    const scoreInfo = mainData?.score || {};
    const reportSummary = mainData?.reportSummary || {};
        const creditAccountInfoSummary = mainData?.creditAccountInfoSummary || {};
    const creditAccounts = mainData?.creditAccountInformationDetails || [];
    const creditEnquiryDetails = mainData?.creditEnquiryDetails || [];
    
    // Analysis sections - check multiple possible locations
    const analysis = mainData?.analysis ||
                    reportData?.analysis || 
                    reportData?.raw_data?.analysis ||
                    reportData?.report_json?.data?.raw_data?.analysis ||  // Report History structure
                    reportData?.data?.raw_data?.analysis ||  // Direct from database
                    reportData?.report_json?.analysis ||
                    reportData?.api_response?.analysis ||
                    reportData?.data?.analysis ||
                    {};
    const creditHistory = analysis?.creditHistory || {};
    const accounts = analysis?.accounts || {};
    const creditUtilization = analysis?.creditUtilization || {};
    const paymentHistory = analysis?.paymentHistory || {};
    const enquiries = analysis?.enquiries || {};
    
    // Enhanced debug logging to see what data we're getting
    console.log('=== EXPERIAN OUTPUT COMPONENT DEBUG ===');
    console.log('Experian Output - Full Report Data:', reportData);
    console.log('Experian Output - Report Data Keys:', Object.keys(reportData || {}));
    console.log('PDF Download URL Prop:', pdfDownloadUrl);
    console.log('PDF Password Hint Prop:', pdfPasswordHint);
    console.log('Checking PDF URLs in reportData:', {
        'reportData.pdf_download_url': reportData?.pdf_download_url,
        'reportData.pdf_url': reportData?.pdf_url,
        'reportData.id': reportData?.id,
        'reportData.report_id': reportData?.report_id
    });
    
    // Check nested structures
    console.log('Checking nested structures:');
    console.log('- Has report_json?', !!reportData?.report_json);
    console.log('- Has report_json.data?', !!reportData?.report_json?.data);
    console.log('- Has report_json.data.raw_data?', !!reportData?.report_json?.data?.raw_data);
    console.log('- Has report_json.data.raw_data.data?', !!reportData?.report_json?.data?.raw_data?.data);
    console.log('- Has data.raw_data.data?', !!reportData?.data?.raw_data?.data);
    
    console.log('Experian Output - Extracted Main Data:', mainData);
    console.log('Experian Output - Main Data Keys:', Object.keys(mainData || {}));
    console.log('Experian Output - Extracted Sections:', {
        personalInfo,
        scoreInfo,
        reportSummary,
        creditAccountsCount: creditAccounts.length,
        creditAccounts: creditAccounts.slice(0, 2), // Show first 2 accounts for debugging
        analysis,
        hasPersonalInfo: !!(personalInfo && Object.keys(personalInfo).length > 0),
        hasScoreInfo: !!(scoreInfo && Object.keys(scoreInfo).length > 0),
        hasData: !!(personalInfo.name || scoreInfo.score || (mainData && Object.keys(mainData).length > 0))
    });

    // Helper function to check if a value is empty (null, undefined, or empty string)
    const getValue = (value, defaultValue = 'N/A') => {
        if (value === null || value === undefined) return defaultValue;
        if (typeof value === 'string' && value.trim() === '') return defaultValue;
        return value;
    };

    // Helper function to get score rating based on score value
    const getScoreRating = (score) => {
        const scoreNum = parseInt(score);
        if (scoreNum >= 750) return 'EXCELLENT';
        if (scoreNum >= 650) return 'GOOD';
        if (scoreNum >= 550) return 'FAIR';
        if (scoreNum >= 300) return 'POOR';
        return 'VERY POOR';
    };

    // Helper function to get score severity for Tag component
    const getScoreSeverity = (score) => {
        const scoreNum = parseInt(score);
        if (scoreNum >= 750) return 'success';
        if (scoreNum >= 650) return 'info';
        if (scoreNum >= 550) return 'warning';
        return 'danger';
    };

    // Handle back button
    const handleBack = () => {
        if (onBack && typeof onBack === 'function') {
            onBack();
        }
    };

    // Handle close button
    const handleClose = () => {
        if (onClose && typeof onClose === 'function') {
            onClose();
        }
    };

    // Simplified handlers are now inline in the button onClick

    // Render personalInformation section
    const renderPersonalInformation = () => {
        const employment = personalInfo.employmentInfo || {};
        
        return (
            <div className="grid">
                <div className="col-12 md:col-6">
                    <Fieldset legend="Basic Information" className="h-full">
                        <div className="flex flex-column gap-3">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-user text-primary"></i>
                                <span className="text-600">Name:</span>
                                <span className="font-semibold">{getValue(personalInfo.name)}</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-calendar text-primary"></i>
                                <span className="text-600">Date of Birth:</span>
                                <span className="font-semibold">{
                                    getValue(personalInfo.dateOfBirth) !== 'N/A' 
                                        ? personalInfo.dateOfBirth 
                                        : personalInfo.age 
                                            ? `Age: ${personalInfo.age}` 
                                            : 'N/A'
                                }</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-users text-primary"></i>
                                <span className="text-600">Gender:</span>
                                <span className="font-semibold">{getValue(personalInfo.gender)}</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-phone text-primary"></i>
                                <span className="text-600">Mobile:</span>
                                <span className="font-semibold">{getValue(personalInfo.mobileNumber)}</span>
                            </div>
                            {getValue(personalInfo.alternateMobileNumber) !== 'N/A' && (
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-phone text-primary"></i>
                                    <span className="text-600">Alternate Mobile:</span>
                                    <span className="font-semibold">{personalInfo.alternateMobileNumber}</span>
                                </div>
                            )}
                        </div>
                    </Fieldset>
                </div>

                <div className="col-12 md:col-6">
                    <Fieldset legend="Identity Information" className="h-full">
                        <div className="flex flex-column gap-3">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-id-card text-primary"></i>
                                <span className="text-600">PAN:</span>
                                <span className="font-semibold">{getValue(personalInfo.pan)}</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-id-card text-primary"></i>
                                <span className="text-600">Aadhaar:</span>
                                <span className="font-semibold">{
                                    getValue(personalInfo.aadhaarNumber) !== 'N/A' 
                                        ? personalInfo.aadhaarNumber 
                                        : getValue(personalInfo.uid)
                                }</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-car text-primary"></i>
                                <span className="text-600">Driving License:</span>
                                <span className="font-semibold">{getValue(personalInfo.drivingLicence)}</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-id-card text-primary"></i>
                                <span className="text-600">Voter ID:</span>
                                <span className="font-semibold">{getValue(personalInfo.voterId)}</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-briefcase text-primary"></i>
                                <span className="text-600">Income:</span>
                                {/* '₹0' for an unreported income reads as "declared zero", not
                                    "not reported" — the same defect as the score, in miniature. */}
                                <span className="font-semibold">{formatIncome(employment.income)}</span>
                            </div>
                        </div>
                    </Fieldset>
                </div>

                {personalInfo.address && (
                    <div className="col-12">
                        <Fieldset legend="Address">
                            <div className="flex align-items-start gap-2">
                                <i className="pi pi-home text-primary mt-1"></i>
                                <span className="font-semibold">{personalInfo.address}</span>
                            </div>
                        </Fieldset>
                    </div>
                )}
            </div>
        );
    };

    // Render Score section
    const renderScore = () => {
        // `parseInt(scoreInfo.score || 0)` used to turn a missing score into 0,
        // and 0 fell past every band to the default — so a thin-file or no-hit
        // borrower was shown a giant red "0" tagged "Poor" on a 0%-of-900 gauge,
        // indistinguishable from a real, terrible score (GH #405). Absent and
        // bad are different answers; scoreBand keeps them apart.
        const band = scoreBand(scoreInfo.score);
        return (
            <div className="grid">
                <div className="col-12 md:col-6 lg:col-4">
                    <div className="surface-card shadow-2 border-round-lg p-4 h-full">
                        <div className="flex flex-column align-items-center">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-chart-line text-primary" style={{ fontSize: '1.5rem' }}></i>
                                <span className="text-xl font-semibold">Credit Score</span>
                            </div>
                            <div className={`text-xl font-bold mb-3 ${band.color}`}>
                                {band.display}
                            </div>
                            {/* No gauge when there is no score — a 0%-filled bar
                                is itself a reading, and there is nothing to read. */}
                            {band.available ? (
                                <ProgressBar
                                    value={band.pct}
                                    showValue={false}
                                    className="w-full mb-3"
                                    style={{ height: '10px' }}
                                    aria-label={`Credit score ${band.score} of ${SCORE_MAX}`}
                                />
                            ) : null}
                            <Tag severity={band.severity} value={band.label} className="text-lg px-3 py-1" />
                            {!band.available ? (
                                <div className="text-600 text-sm mt-2 text-center">
                                    Experian did not return a score for this applicant.
                                </div>
                            ) : null}
                            <div className="text-600 text-sm mt-3">
                                <span className="font-semibold">{scoreInfo.scoreName || 'EXPERIAN'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {scoreInfo.scoringFactors && (
                    <div className="col-12 md:col-6 lg:col-8">
                        <div className="surface-card shadow-2 border-round-lg p-4 h-full">
                            <h6 className="text-primary mb-3">Scoring Factors</h6>
                            <div className="flex flex-column gap-2">
                                {scoreInfo.scoringFactors.map((factor, index) => (
                                    <div key={index} className="flex align-items-center gap-2">
                                        <i className="pi pi-check-circle text-green-500"></i>
                                        <span>{factor}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render reportSummary section
    const renderReportSummary = () => {
        const creditSummary = reportSummary.creditAccountSummary || {};
        const balanceSummary = reportSummary.currentBalanceAccountSummary || {};
        const creditEnquirySummary = reportSummary.creditEnquirySummary || {};
        const nonCreditEnquirySummary = reportSummary.nonCreditEnquirySummary || {};
        return (
            <div className="grid">
                <div className="col-12 lg:col-6">
                    <Fieldset legend="Credit Account Summary">
                        <div className="grid">
                            <div className="col-6">
                                <div className="text-600 text-sm">Total Accounts</div>
                                <div className="text-xl font-bold text-primary">{creditSummary.totalAccounts || '0'}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-600 text-sm">Active Accounts</div>
                                <div className="text-xl font-bold text-green-600">{creditSummary.activeAccounts || '0'}</div>
                            </div>
                            <div className="col-6 mt-3">
                                <div className="text-600 text-sm">Closed Accounts</div>
                                <div className="text-xl font-bold text-blue-600">{creditSummary.closedAccounts || '0'}</div>
                            </div>
                            <div className="col-6 mt-3">
                                <div className="text-600 text-sm">Overdue Accounts</div>
                                <div className="text-xl font-bold text-orange-600">{creditSummary.overdueAccounts || '0'}</div>
                            </div>
                        </div>
                    </Fieldset>
                </div>

                <div className="col-12 lg:col-6">
                    <Fieldset legend="Current Balance Summary">
                        <div className="text-center mb-3">
                            <div className="text-600 text-sm mb-2">Total Current Balance</div>
                            <div className="text-xl font-bold text-primary">
                                ₹{parseInt(balanceSummary.totalCurrentBalanceAmount || 0).toLocaleString('en-IN')}
                            </div>
                        </div>
                        <Divider />
                        <div className="grid">
                            <div className="col-6">
                                <div className="text-600 text-sm">Secured</div>
                                <div className="font-semibold">₹{parseInt(balanceSummary.securedAccountsSettlement || 0).toLocaleString('en-IN')}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-600 text-sm">Unsecured</div>
                                <div className="font-semibold">₹{parseInt(balanceSummary.unsecuredAccountsSettlement || 0).toLocaleString('en-IN')}</div>
                            </div>
                        </div>
                    </Fieldset>
                </div>

                <div className="col-12 lg:col-6 mt-3">
                    <Fieldset legend="Credit Enquiry Summary">
                        <div className="grid">
                            <div className="col-6">
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 7 Days:</span>
                                    <Badge value={creditEnquirySummary?.last7Days || '0'} />
                                </div>
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 30 Days:</span>
                                    <Badge value={creditEnquirySummary?.last30Days || '0'} severity="warning" />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 90 Days:</span>
                                    <Badge value={creditEnquirySummary?.last90Days || '0'} severity="info" />
                                </div>
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 180 Days:</span>
                                    <Badge value={creditEnquirySummary?.last180Days || '0'} />
                                </div>
                            </div>
                        </div>
                    </Fieldset>
                </div>

                <div className="col-12 lg:col-6 mt-3">
                    <Fieldset legend="Non-Credit Enquiry Summary">
                        <div className="grid">
                            <div className="col-6">
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 7 Days:</span>
                                    <Badge value={nonCreditEnquirySummary?.last7Days || '0'} />
                                </div>
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 30 Days:</span>
                                    <Badge value={nonCreditEnquirySummary?.last30Days || '0'} severity="warning" />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 90 Days:</span>
                                    <Badge value={nonCreditEnquirySummary?.last90Days || '0'} severity="info" />
                                </div>
                                <div className="flex justify-content-between mb-2">
                                    <span className="text-600">Last 180 Days:</span>
                                    <Badge value={nonCreditEnquirySummary?.last180Days || '0'} />
                                </div>
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </div>
        );
    };

    // Render creditAccountInfoSummary section
    const renderCreditAccountInfoSummary = () => {
        if (!creditAccountInfoSummary || Object.keys(creditAccountInfoSummary).length === 0) {
            return (
                <div className="text-center p-4">
                    <i className="pi pi-info-circle text-3xl text-400 mb-2"></i>
                    <p className="text-600">No credit account summary information available</p>
                </div>
            );
        }

        return (
            <div className="grid">
                {Object.entries(creditAccountInfoSummary).map(([key, value]) => (
                    <div key={key} className="col-12 md:col-6 lg:col-4">
                        <div className="surface-50 border-round p-3">
                            <div className="text-600 text-sm mb-2">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                            <div className="text-xl font-semibold">{safeRender(value)}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render payment history modal similar to Equifax
    const showPaymentHistoryModal = (account) => {
        const accountNumber = account.creditAccountTerms?.accountNumber || 'N/A';
        const paymentHistory = account.dpdPaymentHistory || {};
        
        setSelectedAccountNumber(accountNumber);
        setSelectedAccountHistory(paymentHistory);
        setPaymentHistoryModal(true);
    };

    // Render payment history in modal
    const renderPaymentHistoryModal = () => {
        if (!selectedAccountHistory || Object.keys(selectedAccountHistory).length === 0) {
            return <p className="text-center text-600">No payment history available</p>;
        }

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        // Payment status explanation
        const getPaymentStatusInfo = (value) => {
            const days = parseInt(value || 0);
            if (days === 0) return { label: 'On Time', severity: 'success', icon: 'pi-check-circle' };
            if (days <= 30) return { label: `${days} Days Late`, severity: 'warning', icon: 'pi-exclamation-triangle' };
            if (days <= 60) return { label: `${days} Days Late`, severity: 'warning', icon: 'pi-exclamation-triangle' };
            if (days <= 90) return { label: `${days} Days Late`, severity: 'danger', icon: 'pi-times-circle' };
            return { label: `${days}+ Days Late`, severity: 'danger', icon: 'pi-times-circle' };
        };

        return (
            <div className="payment-history-container p-3">
                {/* Legend */}
                <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                        <Tag severity="success" value="On Time (0 DPD)" />
                        <Tag severity="warning" value="Late (1-60 DPD)" />
                        <Tag severity="danger" value="Default (60+ DPD)" />
                    </div>
                </div>

                {/* Payment History by Year */}
                {Object.entries(selectedAccountHistory).map(([year, monthsData]) => (
                    <Card key={year} className="mb-3">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <span className="font-bold text-lg">{year}</span>
                            <Badge value={`${monthsData.length} months`} severity="info" />
                        </div>
                        
                        <div className="grid">
                            {monthsData.map((monthData, idx) => {
                                const value = parseInt(monthData.value || 0);
                                const statusInfo = getPaymentStatusInfo(value);
                                
                                return (
                                    <div key={idx} className="col-6 md:col-3 lg:col-2 mb-2">
                                        <div className="text-center p-2 border-round surface-100">
                                            <div className="text-sm font-semibold mb-1">{monthData.month}</div>
                                            <Tag 
                                                severity={statusInfo.severity} 
                                                value={value === 0 ? 'OK' : `${value}D`}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    // Render creditAccountInformationDetails section with DataTable
    const renderCreditAccountInformationDetails = () => {
        if (!creditAccounts || creditAccounts.length === 0) {
            return (
                <div className="text-center p-4">
                    <i className="pi pi-inbox text-4xl text-400 mb-3"></i>
                    <p className="text-600">No credit accounts found</p>
                </div>
            );
        }

        // Helper function to calculate balance tenure
        const calculateBalanceTenure = (dateOpened, repaymentTenure) => {
            if (!dateOpened || dateOpened === 'N/A' || !repaymentTenure || repaymentTenure === 'N/A') {
                return 'N/A';
            }
            
            try {
                // Parse the date opened (supports various date formats)
                const openedDate = new Date(dateOpened);
                const currentDate = new Date();
                
                // Validate the date
                if (isNaN(openedDate.getTime())) {
                    return 'N/A';
                }
                
                // Calculate months elapsed since date opened
                const yearsDiff = currentDate.getFullYear() - openedDate.getFullYear();
                const monthsDiff = currentDate.getMonth() - openedDate.getMonth();
                const monthsElapsed = (yearsDiff * 12) + monthsDiff;
                
                // Parse repayment tenure (assuming it's in months)
                const totalTenure = parseInt(repaymentTenure);
                
                if (isNaN(totalTenure)) {
                    return 'N/A';
                }
                
                // Calculate balance tenure (Repayment Tenure - Months Elapsed)
                const balanceTenure = totalTenure - monthsElapsed;
                
                // Return 0 if negative (loan should be completed)
                return balanceTenure > 0 ? balanceTenure : 0;
            } catch (error) {
                console.error('Error calculating balance tenure:', error);
                return 'N/A';
            }
        };

        // Transform credit accounts data for DataTable
        const tableData = creditAccounts.map((account, index) => {
            const accountInfo = account.creditAccountInformation || {};
            const accountTerms = account.creditAccountTerms || {};
            const accountDescription = account.creditAccountDescription || {};
            const accountDetails = account.creditAccountDetails || {};
            const paymentHistory = account.dpdPaymentHistory || {};
            const consumerDetails = account.consumerDetails || {};
            
            // Calculate balance tenure using the formula: Repayment Tenure - Time Elapsed since Date Opened
            const calculatedBalanceTenure = calculateBalanceTenure(
                accountTerms.dateOpened,
                accountDetails.repaymentTenure
            );
            
            return {
                id: index,
                accountHolderName: accountInfo.accountHolderName || consumerDetails?.name || personalInfo.name || 'N/A',
                accountNumber: accountTerms.accountNumber || 'N/A',
                bankName: accountInfo.lender || 'N/A',
                typeOfLoan: accountInfo.accountType || accountDescription.loanType || 'N/A',
                ownerType: accountDescription.ownershipIndicator || 'Individual',
                dateOpened: accountTerms.dateOpened || 'N/A',
                loanAmount: parseInt(accountDescription.highestCredit || 0),
                interestRate: accountTerms.rateOfInterest || 'N/A',
                emi: parseInt(accountDetails.emiAmount || 0),
                repaymentTenure: accountDetails.repaymentTenure || 'N/A',
                balanceTenure: calculatedBalanceTenure !== 'N/A' ? calculatedBalanceTenure : (accountDetails.remainingTenure || 'N/A'),
                loanOutstandingBalance: parseInt(accountDescription.currentBalance || 0),
                amountOverdue: parseInt(accountDescription.amountOverdue || 0),
                lastPaymentDate: accountDescription.lastPaymentDate || 'N/A',
                creditLimit: parseInt(accountDetails.creditLimitAmount || 0),
                dateReported: accountDescription.dateReported || 'N/A',
                accountStatus: accountDescription.accountStatus || 'Unknown',
                paymentHistory: paymentHistory,
                fullAccount: account
            };
        });

        // Column templates for better formatting
        const formatCurrency = (value) => {
            return `₹${value.toLocaleString('en-IN')}`;
        };

        const accountNumberTemplate = (rowData) => {
            return (
                <div className="flex align-items-center gap-2">
                    <span className="font-semibold">{rowData.accountNumber}</span>
                </div>
            );
        };

        const loanAmountTemplate = (rowData) => {
            return <span className="font-semibold">{formatCurrency(rowData.loanAmount)}</span>;
        };

        const outstandingTemplate = (rowData) => {
            return (
                <span className={`font-bold ${rowData.loanOutstandingBalance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {formatCurrency(rowData.loanOutstandingBalance)}
                </span>
            );
        };

        const overdueTemplate = (rowData) => {
            return (
                <span className={`font-bold ${rowData.amountOverdue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(rowData.amountOverdue)}
                </span>
            );
        };

        const statusTemplate = (rowData) => {
            const severity = rowData.accountStatus === 'ACTIVE' ? 'success' : 
                           rowData.accountStatus === 'CLOSED' ? 'info' : 'warning';
            return <Tag severity={severity} value={rowData.accountStatus} />;
        };

        const actionTemplate = (rowData) => {
            const hasHistory = rowData.paymentHistory && Object.keys(rowData.paymentHistory).length > 0;
            return (
                <Button
                    icon="pi pi-history"
                    className="p-button-text p-button-sm"
                    tooltip="View Payment History"
                    onClick={() => showPaymentHistoryModal(rowData.fullAccount)}
                    disabled={!hasHistory}
                />
            );
        };

        // Row expansion template for additional details
        const rowExpansionTemplate = (data) => {
            const account = data.fullAccount;
            const consumerDetails = account.consumerDetails || {};

            return (
                <div className="p-3">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <Fieldset legend="Consumer Details">
                                <div className="grid">
                                    <div className="col-6">
                                        <div className="text-600 text-sm">Name</div>
                                        <div className="font-semibold">{consumerDetails.name || 'N/A'}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-600 text-sm">Date of Birth</div>
                                        <div className="font-semibold">{consumerDetails.dateOfBirth || 'N/A'}</div>
                                    </div>
                                    <div className="col-6 mt-2">
                                        <div className="text-600 text-sm">Gender</div>
                                        <div className="font-semibold">{consumerDetails.gender || 'N/A'}</div>
                                    </div>
                                    <div className="col-6 mt-2">
                                        <div className="text-600 text-sm">Occupation</div>
                                        <div className="font-semibold">{consumerDetails.occupation || 'N/A'}</div>
                                    </div>
                                </div>
                            </Fieldset>
                        </div>
                        <div className="col-12 md:col-6">
                            <Fieldset legend="Additional Information">
                                <div className="grid">
                                    <div className="col-6">
                                        <div className="text-600 text-sm">Written Off Amount</div>
                                        <div className="font-semibold">₹{parseInt(account.creditAccountDescription?.writtenOffAmount || 0).toLocaleString('en-IN')}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-600 text-sm">Settlement Amount</div>
                                        <div className="font-semibold">₹{parseInt(account.creditAccountDescription?.settlementAmount || 0).toLocaleString('en-IN')}</div>
                                    </div>
                                    <div className="col-12 mt-2">
                                        <div className="text-600 text-sm">Address</div>
                                        <div className="font-semibold">{consumerDetails.address || 'N/A'}</div>
                                    </div>
                                </div>
                            </Fieldset>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <>
                <DataTable
                    value={tableData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    className="p-datatable-striped p-datatable-gridlines"
                    showGridlines
                    stripedRows
                    responsiveLayout="scroll"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                    dataKey="id"
                    header={
                        <div className="flex justify-content-between align-items-center">
                            <span className="text-xl font-bold">Credit Account Information</span>
                            <span className="text-sm text-600">{tableData.length} Account(s)</span>
                        </div>
                    }
                >
                    <Column expander style={{ width: '3em' }} />
                    <Column field="accountHolderName" header="Account Holder Name" sortable />
                    <Column field="accountNumber" header="Account Number" body={accountNumberTemplate} sortable />
                    <Column field="bankName" header="Bank Name" sortable />
                    <Column field="typeOfLoan" header="Type of Loan" sortable />
                    <Column field="ownerType" header="Owner Type" sortable />
                    <Column field="dateOpened" header="Date Opened" sortable />
                    <Column field="loanAmount" header="Loan Amount" body={loanAmountTemplate} sortable />
                    <Column field="interestRate" header="Interest Rate" body={(rowData) => rowData.interestRate ? `${rowData.interestRate}%` : 'N/A'} sortable />
                    <Column field="emi" header="EMI" body={(rowData) => formatCurrency(rowData.emi)} sortable />
                    <Column field="repaymentTenure" header="Repayment Tenure" body={(rowData) => rowData.repaymentTenure ? `${rowData.repaymentTenure} months` : 'N/A'} sortable />
                    <Column field="balanceTenure" header="Calculated Balance Tenure" body={(rowData) => rowData.balanceTenure ? `${rowData.balanceTenure} months` : 'N/A'} sortable />
                    <Column field="loanOutstandingBalance" header="Outstanding Balance" body={outstandingTemplate} sortable />
                    <Column field="amountOverdue" header="Amount Overdue" body={overdueTemplate} sortable />
                    <Column field="lastPaymentDate" header="Last Payment Date" sortable />
                    <Column field="creditLimit" header="Credit Limit" body={(rowData) => formatCurrency(rowData.creditLimit)} sortable />
                    <Column field="dateReported" header="Date Reported" sortable />
                    <Column field="accountStatus" header="Status" body={statusTemplate} sortable />
                    <Column header="Actions" body={actionTemplate} style={{ width: '100px' }} />
                </DataTable>

                {/* Payment History Modal */}
                <Dialog
                    header={`Payment History - Account: ${selectedAccountNumber}`}
                    visible={paymentHistoryModal}
                    onHide={() => {
                        setPaymentHistoryModal(false);
                        setSelectedAccountHistory(null);
                        setSelectedAccountNumber('');
                    }}
                    modal
                    className="w-11 md:w-9 lg:w-7"
                    contentClassName="p-0"
                >
                    {renderPaymentHistoryModal()}
                </Dialog>
            </>
        );
    };


    // Render creditEnquiryDetails section
    const renderCreditEnquiryDetails = () => {
        if (!creditEnquiryDetails || creditEnquiryDetails.length === 0) {
            return (
                <div className="text-center p-4">
                    <i className="pi pi-inbox text-3xl text-400 mb-2"></i>
                    <p className="text-600">No credit enquiries found</p>
                </div>
            );
        }

        const columns = [
            { field: 'lender', header: 'Lender/Institution' },
            { field: 'enquiryDate', header: 'Date' },
            { field: 'enquiryPurpose', header: 'Purpose' },
            { field: 'enquiryAmount', header: 'Amount', body: (rowData) => rowData.enquiryAmount ? `₹${parseInt(rowData.enquiryAmount).toLocaleString('en-IN')}` : 'N/A' }
        ];

        return (
            <DataTable value={creditEnquiryDetails} paginator rows={10} className="p-datatable-sm">
                {columns.map(col => (
                    <Column key={col.field} field={col.field} header={col.header} body={col.body} />
                ))}
            </DataTable>
        );
    };

    // Render creditHistory section
    const renderCreditHistory = () => {
        if (!creditHistory || Object.keys(creditHistory).length === 0) {
            return null;
        }

        const getImpactColor = (status) => {
            switch(status?.toLowerCase()) {
                case 'excellent': return 'green';
                case 'good': return 'blue';
                case 'fair': return 'orange';
                case 'poor': return 'red';
                default: return 'gray';
            }
        };

        return (
            <div className="surface-card shadow-2 border-round-lg p-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h5 className="text-primary m-0">{creditHistory.label || 'Credit History'}</h5>
                    <Tag severity={creditHistory.impactStatus === 'Good' ? 'success' : 'warning'} 
                         value={creditHistory.impactStatus || 'N/A'} />
                </div>
                
                <div className="grid">
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="text-600 text-sm">Age of Accounts</div>
                        <div className="text-xl font-bold text-primary">{creditHistory.ageOfAccounts || 'N/A'}</div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="text-600 text-sm">Active Accounts</div>
                        <div className="text-xl font-bold">{creditHistory.noOfActiveAccounts || '0'}</div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="text-600 text-sm">Impact on Score</div>
                        <div className="font-semibold">{creditHistory.impactOnScore || 'N/A'}</div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="text-600 text-sm">Status</div>
                        <div className={`font-bold text-${getImpactColor(creditHistory.impactStatus)}-600`}>
                            {creditHistory.impactStatus || 'N/A'}
                        </div>
                    </div>
                </div>

                {creditHistory.information && (
                    <div className="mt-3 p-3 surface-100 border-round">
                        <div className="font-semibold mb-2">
                            {typeof creditHistory.information === 'object' && creditHistory.information !== null
                                ? String(creditHistory.information.title || 'Information')
                                : 'Information'}
                        </div>
                        <div className="text-600">
                            {typeof creditHistory.information === 'object' && creditHistory.information !== null
                                ? String(creditHistory.information.message || '')
                                : typeof creditHistory.information === 'string' 
                                    ? creditHistory.information 
                                    : ''}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render accounts section
    const renderAccounts = () => {
        if (!accounts || Object.keys(accounts).length === 0) {
            return null;
        }

        return (
            <div className="surface-card shadow-2 border-round-lg p-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h5 className="text-primary m-0">{accounts.label || 'Accounts'}</h5>
                    <Tag severity={accounts.impactStatus === 'Good' ? 'success' : 'warning'} 
                         value={accounts.impactStatus || 'N/A'} />
                </div>
                
                <div className="grid">
                    <div className="col-12 md:col-4">
                        <div className="text-center">
                            <div className="text-600 text-sm mb-2">Active Accounts</div>
                            <div className="text-xl font-bold text-green-600">{accounts.noOfActiveAccounts || '0'}</div>
                        </div>
                    </div>
                    <div className="col-12 md:col-4">
                        <div className="text-center">
                            <div className="text-600 text-sm mb-2">Closed Accounts</div>
                            <div className="text-xl font-bold text-blue-600">{accounts.noOfClosedAccounts || '0'}</div>
                        </div>
                    </div>
                    <div className="col-12 md:col-4">
                        <div className="text-center">
                            <div className="text-600 text-sm mb-2">Impact on Score</div>
                            <div className="font-semibold">{accounts.impactOnScore || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                {accounts.information && (
                    <div className="mt-3 p-3 surface-100 border-round">
                        <div className="font-semibold mb-2">
                            {typeof accounts.information === 'object' && accounts.information !== null
                                ? String(accounts.information.title || 'Information')
                                : 'Information'}
                        </div>
                        <div className="text-600">
                            {typeof accounts.information === 'object' && accounts.information !== null
                                ? String(accounts.information.message || '')
                                : typeof accounts.information === 'string' 
                                    ? accounts.information 
                                    : ''}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render creditUtilization section
    const renderCreditUtilization = () => {
        if (!creditUtilization || Object.keys(creditUtilization).length === 0) {
            return (
                <div className="text-center p-4">
                    <i className="pi pi-chart-pie text-3xl text-400 mb-2"></i>
                    <p className="text-600">No credit utilization data available</p>
                </div>
            );
        }

        return (
            <div className="surface-card shadow-2 border-round-lg p-4">
                <h5 className="text-primary mb-3">Credit Utilization</h5>
                <div className="grid">
                    {Object.entries(creditUtilization).map(([key, value]) => (
                        <div key={key} className="col-12 md:col-6 lg:col-4 mb-3">
                            <div className="text-600 text-sm mb-2">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                            <div className="font-semibold text-lg">{safeRender(value)}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Render paymentHistory section
    const renderPaymentHistory = () => {
        if (!paymentHistory || Object.keys(paymentHistory).length === 0) {
            return null;
        }

        const ratio = parseInt(paymentHistory.ratio || 0);

        return (
            <div className="surface-card shadow-2 border-round-lg p-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h5 className="text-primary m-0">{paymentHistory.label || 'Payment History'}</h5>
                    <Tag severity={paymentHistory.impactStatus === 'Excellent' ? 'success' : 'warning'} 
                         value={paymentHistory.impactStatus || 'N/A'} />
                </div>
                
                <div className="grid align-items-center">
                    <div className="col-12 md:col-4">
                        <div className="text-center">
                            <Knob 
                                value={ratio} 
                                size={150}
                                strokeWidth={8}
                                valueTemplate={`${ratio}%`}
                                valueColor={`var(--${ratio >= 90 ? 'green' : ratio >= 70 ? 'blue' : ratio >= 50 ? 'orange' : 'red'}-500)`}
                            />
                            <div className="text-600 text-sm mt-2">Payment Success Rate</div>
                        </div>
                    </div>
                    
                    <div className="col-12 md:col-8">
                        <div className="grid">
                            <div className="col-6">
                                <div className="text-600 text-sm">Late Payments</div>
                                <div className="text-xl font-bold text-orange-600">{paymentHistory.noOfLatePayments || '0'}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-600 text-sm">Impact on Score</div>
                                <div className="font-semibold">{paymentHistory.impactOnScore || 'N/A'}</div>
                            </div>
                        </div>
                        
                        {paymentHistory.information && (
                            <div className="mt-3 p-3 surface-100 border-round">
                                <div className="font-semibold mb-2">
                                    {typeof paymentHistory.information === 'object' && paymentHistory.information !== null
                                        ? String(paymentHistory.information.title || 'Information')
                                        : 'Information'}
                                </div>
                                <div className="text-600">
                                    {typeof paymentHistory.information === 'object' && paymentHistory.information !== null
                                        ? String(paymentHistory.information.message || '')
                                        : typeof paymentHistory.information === 'string' 
                                            ? paymentHistory.information 
                                            : ''}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render enquiries section
    const renderEnquiries = () => {
        if (!enquiries || Object.keys(enquiries).length === 0) {
            return null;
        }

        return (
            <div className="surface-card shadow-2 border-round-lg p-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h5 className="text-primary m-0">{enquiries.label || 'Enquiries'}</h5>
                    <Tag severity={enquiries.impactStatus === 'Excellent' ? 'success' : 'warning'} 
                         value={enquiries.impactStatus || 'N/A'} />
                </div>
                
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <div className="text-center">
                            <div className="text-600 text-sm mb-2">Total Enquiries</div>
                            <div className="text-xl font-bold text-primary">{enquiries.totalNoOfEnquiry || '0'}</div>
                        </div>
                    </div>
                    <div className="col-12 md:col-3">
                        <div className="text-center">
                            <div className="text-600 text-sm mb-2">Card Enquiries</div>
                            <div className="text-xl font-bold">{enquiries.noOfCardEnquiry || '0'}</div>
                        </div>
                    </div>
                    <div className="col-12 md:col-3">
                        <div className="text-center">
                            <div className="text-600 text-sm mb-2">Loan Enquiries</div>
                            <div className="text-xl font-bold">{enquiries.noOFLoanEnquiry || '0'}</div>
                        </div>
                    </div>
                    <div className="col-12 md:col-3">
                        <div className="text-center">
                            <div className="text-600 text-sm mb-2">Impact on Score</div>
                            <div className="font-semibold">{enquiries.impactOnScore || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                {enquiries.information && (
                    <div className="mt-3 p-3 surface-100 border-round">
                        <div className="font-semibold mb-2">
                            {typeof enquiries.information === 'object' && enquiries.information !== null
                                ? String(enquiries.information.title || 'Information')
                                : 'Information'}
                        </div>
                        <div className="text-600">
                            {typeof enquiries.information === 'object' && enquiries.information !== null
                                ? String(enquiries.information.message || '')
                                : typeof enquiries.information === 'string' 
                                    ? enquiries.information 
                                    : ''}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="experian-output-component">
            <Toast ref={toast} />
            
            {/* Compact Header - Matching Equifax Style */}
            <div className="grid w-full mb-3">
                <div className="col-12">
                    <div className="flex flex-column sm:flex-row align-items-start sm:align-items-center justify-content-between gap-3 w-full">
                        <div className="col-12 md:col-8">
                            <div className="flex flex-column md:flex-row md:align-items-center gap-2">
                                <h2 className="text-lg font-semibold text-900 m-0">
                                    <i className="pi pi-chart-bar mr-2 text-primary" aria-hidden="true" />
                                    Experian Credit Report
                                </h2>
                                {!hideCredits && serviceDetails && (
                                    <Tag
                                        severity="success"
                                        icon="pi pi-wallet"
                                        value={`Remaining: ${reportData?.remaining_credits || reportData?.data?.remaining_credits || serviceDetails?.available_credits || 'N/A'}`}
                                        className="text-sm"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {onBack && (
                                <Button
                                    label="Back"
                                    icon="pi pi-arrow-left"
                                    className="p-button-sm p-button-secondary"
                                    onClick={handleBack}
                                />
                            )}
                            {onClose && (
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-sm p-button-danger"
                                    onClick={handleClose}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content TabView with all sections */}
            <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>

                {/* 1. Score */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            <i className="pi pi-chart-line"></i>
                            <span>Score</span>
                        </span>
                    }
                >
                    {renderScore()}
                </TabPanel>

                {/* 3. Report Summary */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            <i className="pi pi-file"></i>
                            <span>Report Summary</span>
                        </span>
                    }
                >
                    {renderReportSummary()}
                </TabPanel>

                {/* 4. Credit Account Info Summary */}
                {creditAccountInfoSummary && Object.keys(creditAccountInfoSummary).length > 0 && (
                    <TabPanel
                        header={
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-info-circle"></i>
                                <span>Credit Account Info</span>
                            </span>
                        }
                    >
                        {renderCreditAccountInfoSummary()}
                    </TabPanel>
                )}

                {/* 5. creditAccountInformationDetails */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            <i className="pi pi-credit-card"></i>
                            <span>Credit Accounts ({creditAccounts.length})</span>
                        </span>
                    }
                >
                    {renderCreditAccountInformationDetails()}
                </TabPanel>

                {/* 6. Personal Information */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            <i className="pi pi-user"></i>
                            <span>Personal Info</span>
                        </span>
                    }
                >
                    {renderPersonalInformation()}
                </TabPanel>

                {/* 7. Credit Enquiry Details */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            <i className="pi pi-search"></i>
                            <span>Credit Enquiries</span>
                        </span>
                    }
                >
                    {renderCreditEnquiryDetails()}
                </TabPanel>

                {/* 8. Credit History */}
                {creditHistory && Object.keys(creditHistory).length > 0 && (
                    <TabPanel
                        header={
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-history"></i>
                                <span>Credit History</span>
                            </span>
                        }
                    >
                        {renderCreditHistory()}
                    </TabPanel>
                )}

                {/* 8. accounts */}
                {accounts && Object.keys(accounts).length > 0 && (
                    <TabPanel
                        header={
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-briefcase"></i>
                                <span>Accounts</span>
                            </span>
                        }
                    >
                        {renderAccounts()}
                    </TabPanel>
                )}

                {/* 9. creditUtilization */}
                {creditUtilization && Object.keys(creditUtilization).length > 0 && (
                    <TabPanel
                        header={
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-chart-pie"></i>
                                <span>Credit Utilization</span>
                            </span>
                        }
                    >
                        {renderCreditUtilization()}
                    </TabPanel>
                )}

                {/* 10. paymentHistory */}
                {paymentHistory && Object.keys(paymentHistory).length > 0 && (
                    <TabPanel
                        header={
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-calendar"></i>
                                <span>Payment History</span>
                            </span>
                        }
                    >
                        {renderPaymentHistory()}
                    </TabPanel>
                )}

                {/* 11. enquiries */}
                {enquiries && Object.keys(enquiries).length > 0 && (
                    <TabPanel
                        header={
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-eye"></i>
                                <span>Enquiries</span>
                            </span>
                        }
                    >
                        {renderEnquiries()}
                    </TabPanel>
                )}
            </TabView>

            {/* Payment History Modal */}
            {paymentHistoryModal && selectedAccountHistory && (
                <Dialog 
                    header={`Payment History - ${selectedAccountNumber}`}
                    visible={paymentHistoryModal} 
                    onHide={() => setPaymentHistoryModal(false)}
                    style={{ width: '90vw', maxWidth: '900px' }}
                    modal
                >
                    <div className="grid">
                        {Object.entries(selectedAccountHistory).map(([year, months]) => (
                            <div key={year} className="col-12">
                                <h5 className="text-primary mb-3">{year}</h5>
                                <div className="grid">
                                    {months.map((monthData, idx) => {
                                        const value = parseInt(monthData.value || 0);
                                        let severity = 'success';
                                        let icon = 'pi-check-circle';
                                        let label = 'On Time';
                                        
                                        if (value > 90) {
                                            severity = 'danger';
                                            icon = 'pi-times-circle';
                                            label = `${value} Days Late`;
                                        } else if (value > 60) {
                                            severity = 'danger';
                                            icon = 'pi-exclamation-circle';
                                            label = `${value} Days Late`;
                                        } else if (value > 30) {
                                            severity = 'warning';
                                            icon = 'pi-exclamation-triangle';
                                            label = `${value} Days Late`;
                                        } else if (value > 0) {
                                            severity = 'info';
                                            icon = 'pi-info-circle';
                                            label = `${value} Days Late`;
                                        }
                                        
                                        return (
                                            <div key={idx} className="col-6 md:col-4 lg:col-3">
                                                <div className={`surface-${severity === 'danger' ? 'red' : severity === 'warning' ? 'orange' : severity === 'info' ? 'blue' : 'green'}-50 border-round p-3 text-center`}>
                                                    <div className="text-600 font-semibold mb-2">{monthData.month} {monthData.year}</div>
                                                    <i className={`pi ${icon} text-${severity === 'danger' ? 'red' : severity === 'warning' ? 'orange' : severity === 'info' ? 'blue' : 'green'}-600 text-2xl mb-2`}></i>
                                                    <div className="text-sm font-semibold">{label}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </Dialog>
            )}

            {/* Download PDF Section - matching Equifax style */}
            <div className="mt-3" style={{ textAlign: 'center' }}>
                <Button
                    label="Download Experian Report"
                    icon="pi pi-download"
                    onClick={async () => {
                        // Don't show toast for password hint - it's displayed below the button

                        if (pdfDownloadUrl) {
                            console.log('Opening PDF URL:', pdfDownloadUrl);
                            window.open(pdfDownloadUrl, '_blank');
                        } else if (onDownloadPDF) {
                            console.log('Using onDownloadPDF callback');
                            onDownloadPDF();
                        } else {
                            // Try to generate PDF first, then download
                            const reportId = reportData?.report_id || reportData?.id || reportData?.data?.report_id || reportData?.data?.id;

                            if (reportId) {
                                try {
                                    toast.current?.show({
                                        severity: 'info',
                                        summary: 'Generating PDF',
                                        detail: 'Please wait while we generate your PDF...',
                                        life: 3000
                                    });

                                    const response = await axiosInstance.get(`/user/experian-report/${reportId}/pdf`);

                                    if (response.data.success && response.data.data?.pdf_url) {
                                        console.log('PDF generated, opening URL:', response.data.data.pdf_url);
                                        window.open(response.data.data.pdf_url, '_blank');
                                    } else {
                                        toast.current?.show({
                                            severity: 'error',
                                            summary: 'Error',
                                            detail: response.data.message || 'Failed to generate PDF',
                                            life: 4000
                                        });
                                    }
                                } catch (error) {
                                    console.error('Error generating PDF:', error);
                                    toast.current?.show({
                                        severity: 'error',
                                        summary: 'Error',
                                        detail: error.response?.data?.message || 'Failed to generate PDF',
                                        life: 4000
                                    });
                                }
                            } else {
                                toast.current?.show({
                                    severity: 'error',
                                    summary: 'Error',
                                    detail: 'PDF download not available. No report ID found.',
                                    life: 4000
                                });
                            }
                        }
                    }}
                    className="p-button-sm p-button-primary"
                    style={{
                        fontSize: '0.875rem',
                        padding: '0.5rem 1rem'
                    }}
                />

                {/* PDF Password Hint - positioned directly below download button */}
                {(pdfPasswordHint || pdfDownloadUrl || reportData?.report_id) && (
                    <div className="mt-2" style={{ textAlign: 'center' }}>
                        <Message
                            severity="info"
                            text={pdfPasswordHint || "Last 6 digits of your mobile number + 'MOB'"}
                            style={{
                                fontSize: '0.875rem',
                                maxWidth: '1000px',
                                margin: '0 auto',
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExperianOutputComponent;